import { SB } from '@play-co/replicant';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import {
  getPetAssetKey,
  Species,
  speciesAssetsRatio,
  speciesChatbot,
} from './species';
import {
  createFlexMessageTemplate,
  createFlexContainer,
  createOpenGameAction,
} from './utils';
import { buildSender } from '../sender';

/**
 * Assets for friend request accepted messages
 */
export const friendRequestAcceptedAssets = {
  'background/friendshipV2':
    'private/chatbot/friendship/friendship_message_bg_v2.png',
};

/**
 * Create friend request accepted message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const friendRequestAcceptedMessage = createFlexMessageTemplate(
  SB.object({
    sender: SB.object({
      photo: SB.string(),
      name: SB.string(),
      species: SB.tuple(speciesChatbot),
    }),
    locale: SB.tuple(['en', 'ja']),
  }),
  (data, payload, api) => {
    const sender = data.sender;
    const text = getLocaleConfig(
      data.locale,
    ).friendRequestAccepted.text.replace('{name}', sender.name);

    const icon: Species = api.getAssetPath(
      getPetAssetKey(sender.species),
    ) as Species;

    const template = createFlexContainer(text, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(text, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath('background/friendshipV2'),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1.51:1',
            gravity: 'center',
          },
          {
            type: 'box',
            layout: 'vertical',
            position: 'absolute',
            width: '80%',
            height: '70%',
            offsetBottom: '30%',
            alignItems: 'center',
            justifyContent: 'flex-end',
            contents: [
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'image',
                    url: icon,
                    aspectRatio: speciesAssetsRatio[icon],
                    aspectMode: 'fit',
                    position: 'absolute',
                    gravity: 'bottom',
                    align: 'center',
                    size: 'full',
                  },
                ],
                position: 'absolute',
                width: '35%',
                height: '100%',
                justifyContent: 'flex-end',
                alignItems: 'center',
                offsetStart: '44%',
              },
            ],
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: sender.photo,
                aspectRatio: '1:1',
                aspectMode: 'cover',
                gravity: 'center',
                size: 'full',
              },
            ],
            position: 'absolute',
            height: '18%',
            width: '12%',
            offsetTop: '5.5%',
            offsetStart: '3.98%',
            cornerRadius: 'sm',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text,
                color: '#435289',
                wrap: true,
                align: 'start',
                size: '12px',
                weight: 'bold',
                position: 'relative',
                gravity: 'center',
                scaling: true,
                maxLines: 2,
                adjustMode: 'shrink-to-fit',
              },
            ],
            position: 'absolute',
            width: '75%',
            height: '16%',
            offsetTop: '7%',
            offsetStart: '18%',
            justifyContent: 'center',
            alignItems: 'flex-start',
          },
        ],
        paddingAll: '0px',
      },
    });

    // Add sender to message
    template['sender'] = buildSender({
      name: sender.name,
      iconUrl: sender.photo,
    });

    return template;
  },
);
